import { notification } from 'antd';
import React, { Suspense, useEffect, useMemo } from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import ErrorBoundary from '@/components/ErrorBoundary';
import Loading from '@/components/Loading';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import { useChainShortName, useUserAddr } from '@/hooks/web3/useChain';
import LayoutComponent from '@/layout';
import PageLayout from '@/layout/PageLayout';

import { RouteBasePath } from '@/types/routers';
import { useTitle } from 'ahooks';
import GlobalComponent from './GlobalComponent';
import GlobalEffect from './GlobalEffect';
import { mobileRouters, routers } from './routers';

notification.config({
  bottom: 88,
});
// eslint-disable-next-line react/display-name, @typescript-eslint/no-explicit-any
const blockerWrap = (Component: React.ComponentType<any>) => (props: any) => {
  const location = useLocation();
  const { isMobile } = useMediaQueryDevice();
  const currentRoute = useMemo(() => location.pathname, [location.pathname]);
  const isShowBlocker = useMemo(() => {
    return isMobile && currentRoute === '/blocker';
  }, [currentRoute, isMobile]);
  return isShowBlocker ? <div {...props}></div> : <Component {...props} />;
};

const WrappedLayoutComponent = blockerWrap(LayoutComponent);

function App(): JSX.Element {
  const chainShortName = useChainShortName();
  const { deviceType, isMobile } = useMediaQueryDevice();
  const defaultUrl = useMemo(() => `/trade/${chainShortName}`, [chainShortName]);

  useEffect(() => {
    window.document.documentElement.className = deviceType;
  }, [deviceType]);
  const NavigateToDefaultPage = useMemo(() => {
    return <Navigate to={defaultUrl} />;
  }, []);
  const NavigateToDefaultPageMobile = useMemo(() => {
    return <Navigate to={defaultUrl} />;
  }, []);

  useEffect(() => {
    // adapt mobile height
    const documentHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--doc-height', `${window.innerHeight}px`);
      doc.style.setProperty('--doc-width', `${window.innerWidth}px`);
    };
    window.addEventListener('resize', documentHeight);
    documentHeight();
  }, []);

  const location = useLocation();
  useEffect(() => {
    const layers = location.pathname.split('/').length;
    if (layers > 2) {
      document.body.classList.add('second-layer');
    } else {
      document.body.classList.remove('second-layer');
    }
  }, [location.pathname]);
  const userAddr = useUserAddr();
  useEffect(() => {
    if (userAddr) {
      gtag('event', 'connect_wallet_address', {
        address: `@${userAddr}`,
      });
    }
  }, [userAddr]);

  useTitle('SynFutures');

  return (
    <div className={`App ${deviceType}`}>
      <WrappedLayoutComponent>
        <ErrorBoundary>
          {/* <DevTool /> */}
          <Suspense
            fallback={
              <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
                <Loading spinning size="large" />
              </div>
            }>
            <GlobalEffect></GlobalEffect>
            <GlobalComponent></GlobalComponent>
            <Routes>
              <Route>
                {isMobile ? (
                  <Route path="/" element={<PageLayout />}>
                    {mobileRouters.map((item, i) => {
                      return <Route key={i} path={item.path} element={item.element} />;
                    })}
                    <Route path="tgp" element={<Navigate to={'/blocker'} />}></Route>
                    <Route path={RouteBasePath.launchpad} element={<Navigate to={'/blocker'} />}></Route>
                    <Route path="odyssey" element={<Navigate to={'/blocker'} />}></Route>
                    <Route path="odyssey/:referralCode" element={<Navigate to={'/blocker'} />}></Route>
                    <Route path="" element={NavigateToDefaultPageMobile}></Route>
                  </Route>
                ) : (
                  <Route path="/" element={<PageLayout />}>
                    {routers.map((item, i) => {
                      return <Route key={i} path={item.path} element={item.element} />;
                    })}
                    <Route path="" element={NavigateToDefaultPage}></Route>
                  </Route>
                )}
                <Route path="*" element={isMobile ? NavigateToDefaultPageMobile : NavigateToDefaultPage} />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </WrappedLayoutComponent>
    </div>
  );
}

export default App;
